import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import SEO from '@/components/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Copy, 
  CheckCircle, 
  MessageCircle, 
  Users, 
  Heart, 
  DollarSign,
  Sparkles,
  ArrowRight,
  Mail,
  Clock,
  Home,
  Shield,
  Eye,
  Loader2
} from 'lucide-react';
import { referralService } from '@/services/referralService';

interface CopyableCardProps {
  title: string;
  content: string;
  badge?: string;
}

const CopyableCard = ({ title, content, badge }: CopyableCardProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <Card className="bg-card/50 border-border/50 hover:border-primary/30 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-foreground">{title}</h4>
            {badge && (
              <span className="text-xs text-primary px-2 py-0.5 rounded-full">
                {badge}
              </span>
            )}
          </div>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleCopy}
            className="text-muted-foreground hover:text-primary"
          >
            {copied ? (
              <CheckCircle className="h-4 w-4 text-primary" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            <span className="ml-2 text-sm">{copied ? 'Copied!' : 'Tap to copy'}</span>
          </Button>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
          {content}
        </p>
      </CardContent>
    </Card>
  );
};

const PartnerGettingStarted = () => {
  const [searchParams] = useSearchParams();
  const [linkCopied, setLinkCopied] = useState(false);

  // First check URL params (from activation page)
  const codeFromUrl = searchParams.get('code');
  const linkFromUrl = searchParams.get('link');

  // Fetch from service if not in URL params
  const { data: sourceInfo, isLoading } = useQuery({
    queryKey: ['referralSourceInfo'],
    queryFn: () => referralService.getReferralSourceInfo(),
    enabled: !codeFromUrl, // Only fetch if no code in URL
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Determine referral code and link (now points to /welcome)
  const referralCode = codeFromUrl || sourceInfo?.referralCode || '';
  const referralLink = linkFromUrl 
    ? decodeURIComponent(linkFromUrl) 
    : referralCode 
      ? referralService.generateWelcomeLink(referralCode) 
      : '';

  const copyReferralLink = async () => {
    if (!referralLink) {
      toast.error('Referral link not available');
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      setLinkCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const explanations = {
    thirtySecond: `SeniorThrive is a simple way to help older adults stay safe, independent, and connected at home.

It helps families spot safety risks in the home, understand day-to-day wellness, and stay aligned instead of guessing or worrying.

Everyone sees the same shared dashboard, so there's less confusion and more confidence.

It's not about monitoring or taking control. It's about helping people thrive at home longer, together.`,
    
    short: `SeniorThrive helps older adults stay safe and independent at home while keeping families aligned. It turns home safety and wellness into something everyone can see, understand, and act on together.`,
    
    optionalClose: `If you'd like, I can share a link so you can explore it and see if it's a fit for your situation.`
  };

  const partnerQuote = `This helps families see what I see and stay aligned between visits.`;

  return (
    <>
      <SEO
        title="Partner Getting Started Guide - SeniorThrive"
        description="You're officially a SeniorThrive Referral Partner. Learn how to share SeniorThrive and start earning referral rewards."
        noindex
      />
      
      <div className="min-h-screen bg-gradient-card">
        {/* Hero Section */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                You're Activated. Here's What to Do Next.
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                You're officially a SeniorThrive Referral Partner.
                <br />
                You now have a simple way to help families thrive at home, and earn along the way.
              </p>
            </div>

            <Card className="border-primary/20 bg-card/80 backdrop-blur-sm mb-8">
              <CardContent className="p-6 text-center">
                <p className="text-xl font-medium text-foreground">
                  Here's how to get started.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Start With a Simple Conversation */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg">
                <MessageCircle className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                Start With a Simple Conversation
              </h2>
            </div>
            
            <p className="text-muted-foreground mb-8">
              When the moment is right, keep it human and straightforward.
              <br />
              Use one of the explanations below, depending on how much time you have.
            </p>

            <div className="space-y-4">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  How to Explain SeniorThrive
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Use this when a family asks, "So what is SeniorThrive?" or when you sense it could be helpful.
                </p>
              </div>

              <CopyableCard 
                title="30-Second Explanation"
                content={explanations.thirtySecond}
              />
              
              <CopyableCard 
                title="Short Version"
                content={explanations.short}
                badge="15 seconds"
              />
              
              <CopyableCard 
                title="Optional Close"
                content={explanations.optionalClose}
              />
            </div>
          </div>
        </section>

        {/* Share SeniorThrive Where It Naturally Fits */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                Share SeniorThrive Where It Naturally Fits
              </h2>
            </div>
            
            <p className="text-muted-foreground mb-6">
              SeniorThrive works best when it's introduced at the right moment.
            </p>

            <p className="text-foreground font-medium mb-4">You might share it when:</p>
            
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border/50">
                <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">A family is worried about safety at home</span>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border/50">
                <Home className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">An older adult wants to stay independent longer</span>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border/50">
                <Heart className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">Care feels fragmented or stressful</span>
              </div>
              <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border/50">
                <Eye className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">Everyone wants clarity and alignment</span>
              </div>
            </div>

            <Card className="border-primary/20">
              <CardContent className="p-5">
                <p className="text-foreground font-medium">
                  This is not about pushing a product.
                  <br />
                  <span className="text-muted-foreground font-normal">It's about offering a better starting point.</span>
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Use SeniorThrive as a Value-Add */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                Use SeniorThrive as a Value-Add for Your Clients
              </h2>
            </div>
            
            <p className="text-muted-foreground mb-6">SeniorThrive helps families:</p>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-foreground">See home safety risks clearly</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-foreground">Stay aligned through a shared dashboard</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-foreground">Build confidence around aging at home</span>
              </li>
            </ul>

            <p className="text-muted-foreground mb-4">Many partners describe it simply as:</p>
            
            <CopyableCard 
              title="Partner Quote"
              content={partnerQuote}
            />
          </div>
        </section>

        {/* Earn Referral Rewards */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                Earn Referral Rewards Automatically
              </h2>
            </div>
            
            <p className="text-muted-foreground mb-8">
              When someone signs up through your referral link and upgrades to a paid plan, you earn.
            </p>

            <Card className="mb-8 overflow-hidden">
              <CardContent className="p-0">
                <div className="bg-muted/50 px-5 py-3 border-b border-border">
                  <h3 className="font-bold text-foreground">What you earn</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left py-3 px-5 font-medium text-foreground">Plan</th>
                        <th className="text-left py-3 px-5 font-medium text-foreground">Referral Reward</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border/50">
                        <td className="py-3 px-5 text-foreground font-medium">Essential</td>
                        <td className="py-3 px-5 text-muted-foreground">20% of net subscription revenue for up to 12 months</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-5 text-foreground font-medium">Family</td>
                        <td className="py-3 px-5 text-muted-foreground">25% of net subscription revenue for up to 12 months</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3 text-sm text-muted-foreground">
              <p className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Earnings accrue monthly while the account remains active
              </p>
              <p className="flex items-start gap-2">
                <span className="text-primary">•</span>
                Payouts are issued when your balance reaches $50, or every six months
              </p>
              <p className="flex items-start gap-2">
                <span className="text-primary">•</span>
                No invoices, follow-ups, or micro-payments
              </p>
            </div>
          </div>
        </section>

        {/* Your Referral Link */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Your Referral Link
              </h2>
              <p className="text-muted-foreground">
                Use this link anytime you want to invite someone to SeniorThrive.
              </p>
            </div>

            {isLoading && !codeFromUrl ? (
              <Card className="border-border bg-card">
                <CardContent className="p-6 text-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-muted-foreground">Loading your referral link...</p>
                </CardContent>
              </Card>
            ) : referralLink ? (
              <Card className="border-primary/30 bg-card">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <code className="flex-1 bg-muted/50 px-4 py-3 rounded-lg border border-border text-sm font-mono break-all">
                      {referralLink}
                    </code>
                    <Button 
                      onClick={copyReferralLink}
                      size="lg"
                      className="bg-gradient-cta hover:opacity-90"
                    >
                      {linkCopied ? (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5 mr-2" />
                          Tap to Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-4 text-center">
                    Your link is unique to you and tracks referrals automatically.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border bg-card">
                <CardContent className="p-6 text-center">
                  <p className="text-muted-foreground mb-4">
                    No referral link found. You may need to activate your partner account first.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button asChild variant="outline">
                      <Link to="/affiliate">View Affiliate Portal</Link>
                    </Button>
                    <Button asChild>
                      <Link to="/partners">Become a Partner</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* Keep It Simple */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
              Keep It Simple
            </h2>
            
            <Card className="mb-8">
              <CardContent className="p-6">
                <p className="text-foreground font-medium mb-4">The partners who get the most value:</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">Share SeniorThrive with people they already work with</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">Keep the explanation clear and grounded</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">Focus on outcomes, not features</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="text-center">
              <p className="text-lg text-foreground font-medium">
                Start with one conversation.
                <br />
                One family.
                <br />
                One moment where clarity helps.
              </p>
            </div>
          </div>
        </section>

        {/* Need Support */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                Need Support?
              </h2>
            </div>
            
            <p className="text-muted-foreground mb-4">If you have questions about:</p>
            
            <ul className="space-y-2 text-muted-foreground mb-6">
              <li className="flex items-center gap-2">
                <span className="text-primary">•</span>
                When to introduce SeniorThrive
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary">•</span>
                How referral rewards work
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary">•</span>
                Whether SeniorThrive fits a specific situation
              </li>
            </ul>

            <p className="text-foreground">
              Contact us anytime at{' '}
              <a 
                href="mailto:support@seniorthrive.com" 
                className="text-primary hover:underline font-medium"
              >
                support@seniorthrive.com
              </a>
            </p>
          </div>
        </section>

        {/* Closing Quote */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <blockquote className="text-xl md:text-2xl text-foreground mb-4">
              "Aging is inevitable.
              <br />
              <span className="text-primary">Thriving at home is a choice.</span>"
            </blockquote>
            <p className="text-muted-foreground text-lg">
              You're now part of helping families make that choice with confidence.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Button asChild size="lg" className="bg-gradient-cta hover:opacity-90">
                <Link to="/affiliate">
                  View Affiliate Portal
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/partners">
                  Back to Partners
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PartnerGettingStarted;
